import { useState } from 'react';
import {
  studentEditErrors, familyNameErrors, normaliseStudentPatch, normaliseFamilyPatch,
  studentStatus, studentDurationMin, lessonSettingsErrors,
} from '../domain/people';
import { clampAmountDecimals } from '../domain/money';
import { tagKind, tagMembers } from '../domain/tags';
import { SWATCHES } from '../domain/categories';
import { freeId } from '../domain/freeId';
import { coreSync } from '../lib/core';

// Editing a student, and the person on their family account (#120).
//
// MMS's student page has four pencils, each opening a right-hand drawer that
// owns a slice of the document, plus a fifth reached from the family contact
// card (#120). The slices do not line up with the Add New Student wizard's
// steps — Time Zone, Student Since, School, Referrer, Skill Level and the tag
// fields have a drawer of their own — so this is not the wizard in edit mode.
//
// One state machine over five forms rather than five hooks: a teacher can have
// only one drawer open, the drawers share their refusal discipline, and two of
// them write to two different documents.
//
// No unsaved-changes guard: these drawers do not stack (the expense form's
// does, hence `leaveForm` in useExpenseEntryForm.js), and MMS does not guard
// them.

export const SURFACES = Object.freeze({
  profile: 'profile',
  details: 'details',
  note: 'note',
  lesson: 'lesson',
  // The family account's own name, and the only surface here that writes the
  // family document rather than the student. A contact is a `familyContacts`
  // record (#319) edited by `FamilyContactDrawer`, outside this hook. There is
  // no 'contact' surface, so a caller passing it opens nothing.
  family: 'family',
});

// What each drawer opens holding, read off the document it is about.
//
// Everything is a string in the form and a typed value in the document:
// `<input type="date">` wants '' and not null. The patch normalisers in
// domain/people.js convert back.
const seedProfile = (student) => ({
  status: studentStatus(student.status),
  studentType: student.studentType || 'child',
  firstName: student.firstName || '',
  lastName: student.lastName || '',
  gender: student.gender || '',
  birthday: student.birthday || '',
  email: student.email || '',
  phone: student.phone || '',
  smsCapable: Boolean(student.smsCapable),
});

const seedDetails = (student, family, currentTags = []) => ({
  // The family's, not the student's — one zone per account, as MMS models it
  // and where MMS asks for it (see domain/people.js). '' is "the studio's",
  // the null the document holds.
  timeZone: family?.timeZone || '',
  studentSince: student.studentSince || '',
  school: student.school || '',
  referrer: student.referrer || '',
  skillLevel: student.skillLevel || '',
  // ── Tags are memberships, not comma-separated strings (#328, #138) ────────
  //
  // `instrumentTags` and `groupTags` are not in the form, so
  // `normaliseStudentPatch` (keyed on presence) leaves a student's existing
  // strings where they are, and this drawer never produces new ones.
  // `scripts/migrate-tags.mjs` turns a studio's strings into records; until it
  // runs, a legacy string shows in the tags column and cannot be removed here,
  // because it has no id to untick.
  //
  // `newTags` is the wizard's shape: a tag created in the picker mints its id
  // locally and becomes a record on Save, so closing the drawer leaves no
  // orphan tag.
  //
  // `tagIds` is what the picker ticks; `baseTagIds` is what it was ticked to
  // when the drawer opened, and `tagWrites` diffs against that rather than
  // re-reading `tagsFor(student.id)` at Save. `useTags` is a live window, so a
  // membership changed elsewhere between opening and Save would otherwise be
  // diffed against the wrong base and silently reverted. Diffing against the
  // seed writes only what the teacher did in this drawer, the same property
  // `saveTag`'s `arrayUnion`/`arrayRemove` delta has (#198).
  baseTagIds: currentTags.map((t) => t.id),
  tagIds: currentTags.map((t) => t.id),
  newTags: [],
  emailReminders: Boolean(student.emailReminders),
  smsReminders: Boolean(student.smsReminders),
});

const seedNote = (student) => ({ note: student.note || '' });

const seedLesson = (student, currency) => ({
  categoryId: student.categoryId || '',
  categoryLabel: student.categoryLabel || '',
  // `?? ''` because `studentDurationMin` returns null for a document without
  // the field (#352), and a controlled <input> handed null becomes an
  // uncontrolled one. The drawer opens blank and Save is refused until a
  // duration is typed.
  durationMin: studentDurationMin(student.durationMin) ?? '',
  pricingMode: student.pricingMode,
  // Back into the studio's own decimal notation, which the field is typed in:
  // the document holds minor units (src/domain/locale.js).
  rate: coreSync().toAmountField(student.rateMinor || 0, currency),
});

// One field: the account's own name (#319), what an invoice is addressed to,
// never a contact's.
const seedFamily = (family) => ({
  lastName: family?.lastName || '',
});

// What Save refuses on the lesson drawer is `lessonSettingsErrors` in
// domain/people.js, shared with the phones' Lesson Settings drawers.

export default function useStudentEdit({
  student, family, updateStudent, updateFamily, currency = 'USD',
  // The tags half (#328). Defaulted so a caller that has not wired it gets an
  // empty picker rather than a crash.
  tags = [], tagsFor = () => [], saveTag = null, tagsLoaded = true,
}) {
  const [surface, setSurface] = useState(null);
  const [form, setForm] = useState({});
  // What the last Save refused, keyed by field. Empty until the first submit;
  // the drawers are submit-time rather than live, for the reason `entryErrors`
  // in useExpenseEntryForm.js gives.
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const open = (which) => {
    if (!student) return;
    setForm(
      which === SURFACES.profile ? seedProfile(student)
        : which === SURFACES.details ? seedDetails(student, family, tagsFor(student.id))
        : which === SURFACES.note ? seedNote(student)
        : which === SURFACES.lesson ? seedLesson(student, currency)
        : seedFamily(family),
    );
    setErrors({});
    setSurface(which);
  };

  const close = () => { setSurface(null); setErrors({}); };

  // Rate truncates a third decimal rather than rounding it (#308):
  // `fromDecimal` rounds half-up, so `50.999` would store a $51.00 rate.
  // `clampAmountDecimals` is MMS's own editor on its Add Payment drawer (#246):
  // the third keystroke is swallowed. Keyed on `rate` because this setter
  // serves all five surfaces.
  const set = (key, value) => {
    setForm((f) => ({
      ...f,
      [key]: key === 'rate' ? clampAmountDecimals(value, currency) : value,
    }));
    // A field being fixed stops being red as it is fixed, as on the expense
    // form: a stale message under a field that now has a value reads as broken.
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // The category picker writes both halves, as the wizard's does (#110): the
  // id is what a rename, a recolour or a reminder-lead change can reach, and
  // the label is what every screen renders without a second read.
  const chooseCategory = (categories, id) => {
    const chosen = categories.find((c) => c.id === id) || null;
    setForm((f) => ({ ...f, categoryId: chosen?.id || '', categoryLabel: chosen?.label || '' }));
    setErrors((prev) => {
      if (!('categoryId' in prev)) return prev;
      const next = { ...prev };
      delete next.categoryId;
      return next;
    });
  };

  // ── The tag picker's two controls (#328) ──────────────────────────────────
  //
  // Both are the wizard's (`useAddStudentForm.js`), except that unticking has
  // to remove: an existing student leaves tags. `saveTag`'s `membershipDelta`
  // computes both halves (#198), so the write hands it the membership wanted.
  const toggleTag = (id) => setForm((f) => ({
    ...f,
    tagIds: (f.tagIds || []).includes(id)
      ? f.tagIds.filter((x) => x !== id)
      : [...(f.tagIds || []), id],
  }));

  // The id is minted here and the record written on Save, as in the wizard:
  // `saveTag` returns nothing, so a created tag would otherwise have no id to
  // tick by. Closing without saving leaves no tag behind.
  const createTag = (label, kind) => {
    const trimmed = String(label || '').trim();
    if (!trimmed) return;
    const id = freeId('tag');
    setForm((f) => ({
      ...f,
      // `SWATCHES[0]`, the wizard's default and the Tags tab's.
      newTags: [...(f.newTags || []), {
        id, label: trimmed, kind, color: SWATCHES[0], description: '',
      }],
      tagIds: [...(f.tagIds || []), id],
    }));
  };

  // The memberships this drawer would write, one entry per tag that changed.
  // Separated from `save()` so it can be read without running it.
  //
  // A tag the student neither joined nor left is not written: every write is
  // a `set` carrying the tag's label, kind, colour and description, so a no-op
  // would stamp `updatedAt` on every tag a student is in whenever anybody
  // edited their school.
  const tagWrites = () => {
    if (surface !== SURFACES.details || !student) return [];
    // `form.baseTagIds`, not `tagsFor(student.id)`: see `seedDetails`.
    const before = new Set(form.baseTagIds || []);
    const after = new Set(form.tagIds || []);
    const byId = new Map([...tags, ...(form.newTags || [])].map((t) => [t.id, t]));
    const writes = [];
    for (const id of new Set([...before, ...after])) {
      const joined = after.has(id);
      if (before.has(id) === joined) continue;
      const tag = byId.get(id);
      // A tag in `before` but not in the window cannot be described, and
      // `saveTag` writes the whole record, so writing it would blank its label.
      // Skipped; it takes a studio past the tags window (200) to reach.
      if (!tag) continue;
      const members = new Set(tagMembers(tag));
      if (joined) members.add(student.id);
      else members.delete(student.id);
      writes.push({
        id: tag.id,
        label: tag.label,
        kind: tagKind(tag),
        color: tag.color,
        description: tag.description || '',
        studentIds: [...members],
      });
    }
    return writes;
  };

  // What this surface would write, as two patches. Pure enough to be read
  // without running it; the lesson drawer is the one place a form value is
  // converted rather than copied.
  const patches = () => {
    if (surface === SURFACES.profile) {
      return { student: normaliseStudentPatch(form), family: null };
    }
    if (surface === SURFACES.note) {
      return { student: normaliseStudentPatch(form), family: null };
    }
    if (surface === SURFACES.details) {
      // `tagIds`, `baseTagIds` and `newTags` come out with `timeZone`.
      // `normaliseStudentPatch` would drop them anyway, but membership lives on
      // the tag (#138), not the student document.
      const { timeZone, tagIds, baseTagIds, newTags, ...rest } = form;
      return {
        student: normaliseStudentPatch(rest),
        // MMS's sentence under this field: "This time zone will also apply to
        // all contacts and Students in this student's Family." One zone per
        // account, asked for where the teacher is standing.
        family: student?.familyId ? normaliseFamilyPatch({ timeZone }) : null,
      };
    }
    if (surface === SURFACES.lesson) {
      return {
        student: {
          // `pricingMode` is a closed set and `rateMinor` is money, so they are
          // converted as `createStudent` converts them, not normalised as
          // strings.
          ...normaliseStudentPatch({
            categoryId: form.categoryId,
            categoryLabel: form.categoryLabel,
            defaultDurationMin: form.durationMin,
          }),
          pricingMode: coreSync().resolvePricingMode(form.pricingMode).id,
          rateMinor: form.pricingMode === 'none' ? 0 : coreSync().fromDecimal(form.rate || 0, currency),
        },
        family: null,
      };
    }
    return { student: null, family: normaliseFamilyPatch(form) };
  };

  const refusals = () => {
    if (surface === SURFACES.lesson) return lessonSettingsErrors(form, currency);
    if (surface === SURFACES.family) return familyNameErrors(form);
    const errs = studentEditErrors(form);
    // `saveTag` returns early and silently when the tags collection has not
    // been read for this studio — a write against an unseen collection is how
    // `useCategories` once re-created deleted categories. Here silence would
    // let the student patch land and the drawer close without the ticked tag.
    // So the drawer refuses (`attachTags` in useAddStudentForm.js gates the
    // wizard's save instead). MMS has no state in which its tag list has not
    // loaded.
    if (surface === SURFACES.details && !tagsLoaded && tagWrites().length) {
      errs.tagIds = 'The studio’s tags have not finished loading — try again in a moment.';
    }
    return errs;
  };

  const save = async () => {
    const found = refusals();
    setErrors(found);
    // The drawer stays open with the fields marked and nothing is written, as
    // MMS does on the one drawer of its five that refuses anything.
    if (Object.keys(found).length) return false;

    setSaving(true);
    try {
      // The tags first. Each tag is its own batch (`saveTag` commits one), so
      // this Save is several writes and one can fail. Memberships before the
      // student patch means a failure leaves the drawer open, the student
      // untouched and the form re-pressable; the other order would leave a
      // saved student whose tags did not land and a closed drawer (#138).
      const writes = tagWrites();
      if (writes.length && saveTag) {
        // Sequential, as the wizard does it: a failure part way stops rather
        // than firing the rest at a collection that has just refused one.
        for (const write of writes) await saveTag(write);
      }
      const { student: studentPatch, family: familyPatch } = patches();
      if (studentPatch && student) await updateStudent(student.id, studentPatch);
      if (familyPatch && student?.familyId) await updateFamily(student.familyId, familyPatch);
      close();
      return true;
    } finally {
      setSaving(false);
    }
  };

  return {
    surface, form, errors, saving, open, close, set, chooseCategory, save,
    toggleTag, createTag, tagWrites,
  };
}
